import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      navigate(data.role === "staff" ? "/checkin" : "/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="hero-glow flex min-h-screen items-center justify-center bg-ink px-5">
      <Seo noindex title="Admin Login — AB Hunter Consulting USA" siteName="AB Hunter Consulting USA" description="Secure admin portal for AB Hunter Consulting USA." />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-3xl border border-gold/25 bg-ink-card/80 p-8 backdrop-blur-xl sm:p-10"
      >
        <div className="flex flex-col items-center text-center">
          <img src="/assets/logo.png" alt="AB Hunter Consulting USA logo" className="h-16 w-auto object-contain" />
          <h1 className="mt-5 font-display text-2xl font-semibold">Admin Portal</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Manage events, products, registrations & orders.</p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="admin-email">Email</Label>
            <Input id="admin-email" data-testid="admin-email-input" type="email" required autoComplete="email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 border-gold/20 bg-ink" placeholder="admin@abhunter.us" />
          </div>
          <div>
            <Label htmlFor="admin-password">Password</Label>
            <Input id="admin-password" data-testid="admin-password-input" type="password" required autoComplete="current-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1.5 border-gold/20 bg-ink" placeholder="••••••••" />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</p>
          )}
          <Button data-testid="admin-login-submit-button" type="submit" disabled={loading}
            className="w-full rounded-full bg-gold py-6 font-semibold text-ink transition-all hover:bg-gold-hover">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="mr-2 h-4 w-4" /> Sign In</>}
          </Button>
        </form>
        <a href="/" data-testid="admin-back-home-link" className="mt-6 block text-center text-xs text-muted-foreground transition-colors hover:text-gold">
          ← Back to abhunter.us
        </a>
      </motion.div>
    </div>
  );
}
